import { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { isMobile } from 'react-device-detect';
import Modal from 'components/modal';
import Button from 'components/button';
import Notification from 'components/notification';
import Icon from 'components/icons';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import { ReactComponent as ChevronRight } from 'assets/icons/chevron-right.svg';
import { HeaderContainer, ReportingTitle as Title } from 'components/modal/styles';
import { CertificationContext } from 'contexts/certifications';
import UserList from './UserList';
import { AssignVoucherModalProps, ExamOption } from './types';
import {
  AssignmentSummary,
  ChevronIconWrapper,
  ContactSalesLink,
  ContactSalesSpan,
  Content,
  FailedSummaryText,
  ModalActionsSection,
  OutHeaderContent,
  OutHeaderSub,
  OutHeaderTitle,
  SearchForm,
  SearchLabel,
  VoucherModalContainer,
} from './styles';

const AssignVoucherModal = ({
  visible,
  onClose,
  handleContactSales,
}: AssignVoucherModalProps): JSX.Element => {
  const {
    voucherSummary,
    assignableUsers,
    fetchingAssignableUsers,
    getAssignableUsers,
    assignVouchers,
  } = useContext(CertificationContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [userExamSelections, setUserExamSelections] = useState<Record<string, string[]>>({});
  const [assigning, setAssigning] = useState(false);
  const [failedAssignments, setFailedAssignments] = useState<Record<string, string[]>>({});
  const [totalFailedCount, setTotalFailedCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const examOptions: ExamOption[] = useMemo(() => {
    if (!voucherSummary?.exams) return [];
    return voucherSummary.exams
      .filter((exam) => exam.is_enabled)
      .map((exam) => ({
        id: exam.certification_version_id,
        code: exam.code,
        name: exam.name,
      }));
  }, [voucherSummary]);

  const certificationVersionIds = useMemo(() => {
    return examOptions.map((exam) => exam.id);
  }, [examOptions]);

  const resetModalState = useCallback(() => {
    setSelectedUserIds(new Set());
    setUserExamSelections({});
    setFailedAssignments({});
    setTotalFailedCount(0);
    setSearchQuery('');
  }, []);

  useEffect(() => {
    if (visible && !isOpen) {
      setIsOpen(true);
      resetModalState();
      if (certificationVersionIds.length > 0) {
        getAssignableUsers?.({
          certification_version_ids: certificationVersionIds,
        });
      }
    } else if (!visible && isOpen) {
      setIsOpen(false);
    }
  }, [visible, isOpen, certificationVersionIds, getAssignableUsers, resetModalState]);

  const handleModalClose = useCallback(() => {
    setIsOpen(false);
    onClose();
  }, [onClose]);

  const handleUserCheckboxChange = (userId: string, checked: boolean) => {
    setSelectedUserIds((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(userId);
      } else {
        newSet.delete(userId);
        setUserExamSelections((prevSelections) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [userId]: _, ...rest } = prevSelections;
          return rest;
        });
      }
      return newSet;
    });
  };

  const handleExamChange = (userId: string, examId: string, checked: boolean) => {
    setUserExamSelections((prev) => {
      const currentExams = prev[userId] || [];
      if (checked) {
        return {
          ...prev,
          [userId]: [...currentExams, examId],
        };
      } else {
        return {
          ...prev,
          [userId]: currentExams.filter((id) => id !== examId),
        };
      }
    });
  };

  const handleAssign = async () => {
    const assignments = Object.entries(userExamSelections)
      .flatMap(([userId, examIds]) =>
        examIds.map((examId) => ({
          certification_version_id: examId,
          assignee_user_id: userId,
        }))
      )
      .filter((assignment) => assignment.certification_version_id);

    if (assignments.length === 0) {
      Notification({ text: 'Please select at least one exam to assign', type: 'warning' });
      return;
    }

    setAssigning(true);
    setFailedAssignments({});
    setTotalFailedCount(0);
    try {
      const result = await assignVouchers?.(assignments);

      if (result?.success) {
        const voucherText = result.totalAssigned === 1 ? 'voucher' : 'vouchers';
        const userText = usersWithAssignments === 1 ? 'user' : 'users';
        Notification({
          text: `Assigned ${result.totalAssigned} ${voucherText} to ${usersWithAssignments} ${userText}!`,
          type: 'success',
          duration: 8,
        });

        resetModalState();

        if (certificationVersionIds.length > 0) {
          getAssignableUsers?.({
            certification_version_ids: certificationVersionIds,
          });
        }
      } else if (result) {
        const failedText = result.totalFailed === 1 ? 'assignment' : 'assignments';
        Notification({
          text: (
            <span>
              {result.totalFailed} voucher {failedText} failed.{' '}
              <ContactSalesLink onClick={handleContactSales}>Contact sales.</ContactSalesLink> for
              assistance.
            </span>
          ),
          type: 'error',
          duration: 10,
        });

        const apiFailedAssignments = result.failedAssignments || {};

        const newSelectedUserIds = new Set<string>(Object.keys(apiFailedAssignments));

        setUserExamSelections(apiFailedAssignments);
        setSelectedUserIds(newSelectedUserIds);
        setFailedAssignments(apiFailedAssignments);
        setTotalFailedCount(result.totalFailed);

        if (certificationVersionIds.length > 0) {
          getAssignableUsers?.({
            certification_version_ids: certificationVersionIds,
          });
        }
      }
    } catch (error) {
      Notification({
        text: (
          <span>
            {assignmentCount} voucher assignment{assignmentCount !== 1 && 's'} failed.{' '}
            <ContactSalesLink onClick={handleContactSales}>Contact sales.</ContactSalesLink> for
            assistance.
          </span>
        ),
        type: 'error',
      });

      setFailedAssignments({ ...userExamSelections });
      setTotalFailedCount(assignmentCount);
    } finally {
      setAssigning(false);
    }
  };

  const hasAssignments = Object.values(userExamSelections).some(
    (examIds) => examIds && examIds.length > 0
  );

  const eligibleUsers = useMemo(() => {
    const allUsers = assignableUsers || [];

    if (examOptions.length === 0) {
      return [];
    }

    const enabledExamIds = new Set(examOptions.map((exam) => exam.id));

    return allUsers.filter((user) => {
      if (!Array.isArray(user.eligible_certifications)) {
        return true;
      }

      if (user.eligible_certifications.length === 0) {
        return false;
      }

      return user.eligible_certifications.some((examId) => enabledExamIds.has(examId));
    });
  }, [assignableUsers, examOptions]);

  const users = useMemo(() => {
    const allUsers = eligibleUsers;
    if (!searchQuery.trim()) {
      return allUsers;
    }
    const query = searchQuery.toLowerCase();
    return allUsers.filter(
      (user) =>
        user.name?.toLowerCase().includes(query) || user.email?.toLowerCase().includes(query)
    );
  }, [eligibleUsers, searchQuery]);

  const assignmentCount = Object.values(userExamSelections).reduce(
    (sum, examIds) => sum + (examIds?.length || 0),
    0
  );

  const usersWithAssignments = Object.entries(userExamSelections).filter(
    ([userId, examIds]) => selectedUserIds.has(userId) && examIds && examIds.length > 0
  ).length;

  const totalVouchers = voucherSummary?.voucher_balance?.remaining || 0;
  const remainingVouchers = Math.max(0, totalVouchers - assignmentCount);
  const canAssignMore = remainingVouchers > 0;

  return (
    <Modal
      visible={isOpen}
      footer={null}
      width={595}
      data-testid="assign-voucher-modal"
      onCancel={handleModalClose}
      fullscreen={isMobile}
      closeIconColor="var(--white, #fff)"
    >
      <VoucherModalContainer>
        <HeaderContainer isCertsOut={remainingVouchers === 0}>
          {remainingVouchers > 0 ? (
            <Title>{remainingVouchers} vouchers available to assign</Title>
          ) : (
            <OutHeaderContent>
              <OutHeaderTitle>You&apos;re out of vouchers!</OutHeaderTitle>
              <OutHeaderSub>
                To add more,{' '}
                <ContactSalesSpan
                  onClick={handleContactSales}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleContactSales();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label="Contact sales to purchase more vouchers"
                >
                  contact sales
                </ContactSalesSpan>
                <ChevronIconWrapper>
                  <ChevronRight width="9" height="9" strokeWidth="2" aria-hidden="true" />
                </ChevronIconWrapper>
              </OutHeaderSub>
            </OutHeaderContent>
          )}
        </HeaderContainer>

        <Content>
          <SearchLabel htmlFor="user-search">Select users to assign vouchers</SearchLabel>
          <SearchForm
            className="col input-group input-group-solid align-items-center ps-4"
            onSubmit={(e) => e.preventDefault()}
          >
            <SearchIcon />
            <input
              id="user-search"
              type="text"
              className="form-control"
              placeholder="Search"
              aria-label="Search users by name or email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchForm>

          <UserList
            users={users}
            examOptions={examOptions}
            selectedUserIds={selectedUserIds}
            userExamSelections={userExamSelections}
            failedAssignments={failedAssignments}
            onUserCheckboxChange={handleUserCheckboxChange}
            onExamChange={handleExamChange}
            loading={fetchingAssignableUsers || false}
            searchQuery={searchQuery}
            canAssignMore={canAssignMore}
          />
        </Content>

        <ModalActionsSection>
          <AssignmentSummary aria-live="polite" aria-atomic="true">
            {totalFailedCount > 0 ? (
              <FailedSummaryText>
                <Icon icon="Info" fill="var(--error, #FE6D6D)" size="small" />
                {totalFailedCount} voucher assignment{totalFailedCount !== 1 ? 's' : ''} failed
              </FailedSummaryText>
            ) : assignmentCount > 0 ? (
              <span>
                {assignmentCount} voucher{assignmentCount !== 1 ? 's' : ''} will be assigned to{' '}
                {usersWithAssignments} user{usersWithAssignments !== 1 ? 's' : ''}
              </span>
            ) : null}
          </AssignmentSummary>
          <Button
            type="primary"
            disabled={!hasAssignments}
            loading={assigning}
            onClick={handleAssign}
          >
            Assign voucher(s)
          </Button>
        </ModalActionsSection>
      </VoucherModalContainer>
    </Modal>
  );
};

export default AssignVoucherModal;
